import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { uncontrollable } from 'uncontrollable';
import cn from 'classnames';
import { Paper } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import omit from 'lodash/omit';
import defaults from 'lodash/defaults';
import transform from 'lodash/transform';
import mapValues from 'lodash/mapValues';

import { accessor, dateFormat, views as componentViews } from './utils/propTypes';

import localizer from './localizer';
import { notify } from './utils/helpers';
import { navigate, views as viewsConstants } from './utils/constants';
import dates from './utils/dates';
import defaultFormats from './formats';
import viewLabel from './utils/viewLabel';
import moveDate from './utils/move';
import VIEWS from './Views';
import Toolbar from './Toolbar';

function viewNames(_views) {
  return !Array.isArray(_views) ? Object.keys(_views) : _views;
}

function isValidView(view, { views: _views }) {
  const names = viewNames(_views);
  return names.indexOf(view) !== -1;
}

const styles = {
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
  },
};

const now = new Date();

/**
 * react-big-calendar is full featured Calendar component for managing events and dates.
 */
const Calendar = ({
  elementProps,
  popup,
  toolbar,
  view,
  views,
  date,
  step,
  titleAccessor,
  allDayAccessor,
  startAccessor,
  endAccessor,
  events,
  ownerDetail,
  culture,
  components,
  style,
  className,
  paperStyle,
  openNewEvent,
  detailOpenCreate,
  loaded,
  tz,
  onNavigate,
  onView,
  onSelectEvent,
  onSelectSlot,
  messages,
  rtl,
  formats: propFormats,
  onCreate,
  generateEmbedCode,
  ...props
}) => {
  const getViews = useCallback(() => {
    if (Array.isArray(views)) {
      return transform(views, (obj, name) => { obj[name] = VIEWS[name]; }, {});
    }

    if (typeof views === 'object') {
      return mapValues(views, (value, key) => {
        if (value === true) {
          return VIEWS[key];
        }
        return value;
      });
    }

    return VIEWS;
  }, [views]);

  const getView = useCallback(() => {
    const views = getViews();
    return views[view];
  }, [getViews, view]);

  const _navigate = useCallback((action, newDate) => {
    const nextDate = moveDate(action, newDate || date, view);
    onNavigate?.(nextDate, view);

    if (action === navigate.DATE) {
      _viewNavigate(nextDate);
    }
  }, [date, view, onNavigate]);

  const _viewNavigate = useCallback((nextDate) => {
    if (dates.eq(date, nextDate, view, localizer.startOfWeek(culture))) {
      _view(viewsConstants.DAY);
    }
  }, [date, view, culture]);

  const _view = useCallback((newView) => {
    if (newView !== view && isValidView(newView, { views })) {
      onView?.(newView);
    }
  }, [view, views, onView]);

  const _select = useCallback((event) => {
    notify(onSelectEvent, event);
  }, [onSelectEvent]);

  const _selectSlot = useCallback((slotInfo) => {
    notify(onSelectSlot, slotInfo);
  }, [onSelectSlot]);

  const _headerClick = useCallback((clickedDate) => {
    if (view === viewsConstants.MONTH || view === viewsConstants.WEEK) {
      _view(viewsConstants.DAY);
    }
    _navigate(navigate.DATE, clickedDate);
  }, [view, _view, _navigate]);

  const ViewComponent = getView();
  const names = viewNames(views);
  const formats = useMemo(() => defaultFormats(propFormats), [propFormats]);

  const viewComponents = useMemo(() => defaults(
    components[view] || {},
    omit(components, names)
  ), [components, view, names]);

  const ToolbarToRender = components.toolbar || Toolbar;
  
  return (
    <div
      {...elementProps}
      className={cn('rbc-calendar', className, {
        'rbc-rtl': rtl,
      })}
      style={style}
    >
      {toolbar && (
        <ToolbarToRender
          tz={tz}
          date={date}
          view={view}
          views={names}
          label={viewLabel(date, view, formats, culture)}
          onViewChange={_view}
          onNavigate={_navigate}
          messages={messages}
          detailOpenCreate={detailOpenCreate}
          onCreate={onCreate}
          generateEmbedCode={generateEmbedCode}
        />
      )}
      {loaded ? (
        <Paper style={paperStyle}>
          <ViewComponent
            {...props}
            {...formats}
            culture={culture}
            formats={undefined}
            events={events}
            date={date}
            view={view}
            components={viewComponents}
            onNavigate={_navigate}
            onSelectEvent={_select} 
            onSelectSlot={_selectSlot}
            localizer={localizer}
            startAccessor={startAccessor}
            endAccessor={endAccessor}
            step={step}
            titleAccessor={titleAccessor}
            length={props.length}
          />
        </Paper>
      ) : (
        <div style={styles.loading}>
          <CircularProgress size={40} />
        </div>
      )}
    </div>
  );
};

Calendar.propTypes = {
  elementProps: PropTypes.object,
  date: PropTypes.instanceOf(Date),
  view: PropTypes.string,
  events: PropTypes.arrayOf(PropTypes.object),
  onNavigate: PropTypes.func,
  onView: PropTypes.func,
  onSelectSlot: PropTypes.func,
  onSelectEvent: PropTypes.func, // Added: This prop was missing from propTypes
  onSelecting: PropTypes.func,
  views: componentViews,
  toolbar: PropTypes.bool,
  popup: PropTypes.bool,
  popupOffset: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  ]),
  selectable: PropTypes.bool,
  step: PropTypes.number,
  rtl: PropTypes.bool,
  eventPropGetter: PropTypes.func,
  titleAccessor: accessor,
  allDayAccessor: accessor,
  startAccessor: accessor,
  endAccessor: accessor,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  scrollToTime: PropTypes.instanceOf(Date),
  formats: PropTypes.shape({
    dateFormat,
    dayFormat: dateFormat,
    weekdayFormat: dateFormat,
    monthHeaderFormat: dateFormat,
    weekHeaderFormat: dateFormat,
    dayHeaderFormat: dateFormat,
    agendaHeaderFormat: dateFormat,
    selectRangeFormat: dateFormat,
    agendaDateFormat: dateFormat,
    agendaTimeFormat: dateFormat,
    agendaTimeRangeFormat: dateFormat,
  }),
  components: PropTypes.shape({
    event: PropTypes.elementType,
    toolbar: PropTypes.elementType,
    agenda: PropTypes.shape({
      date: PropTypes.elementType,
      time: PropTypes.elementType,
      event: PropTypes.elementType,
    }),
    day: PropTypes.shape({ event: PropTypes.elementType }),
    week: PropTypes.shape({ event: PropTypes.elementType }),
    month: PropTypes.shape({ event: PropTypes.elementType }),
  }),
  messages: PropTypes.shape({
    allDay: PropTypes.node,
    previous: PropTypes.node,
    next: PropTypes.node,
    today: PropTypes.node,
    month: PropTypes.node,
    week: PropTypes.node,
    day: PropTypes.node,
    agenda: PropTypes.node,
    showMore: PropTypes.func,
  }),
  detailOpenCreate: PropTypes.bool,
  openNewEvent: PropTypes.func.isRequired,
  loaded: PropTypes.bool,
  culture: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  paperStyle: PropTypes.object,
  tz: PropTypes.string,
  ownerDetail: PropTypes.object,
};

// Set default props
Calendar.defaultProps = {
  elementProps: {},
  popup: false,
  toolbar: true,
  view: viewsConstants.MONTH,
  views: [viewsConstants.MONTH, viewsConstants.WEEK, viewsConstants.DAY, viewsConstants.AGENDA],
  date: now,
  step: 30,
  titleAccessor: 'title',
  allDayAccessor: 'allDay',
  startAccessor: 'start',
  endAccessor: 'end',
  events: [],
  culture: '',
  components: {},
  style: {},
  className: '',
  paperStyle: {},
  loaded: false,
  tz: 'UTC',
  messages: {},
  detailOpenCreate: false,
  ownerDetail: null,
  onSelectEvent: () => {}, // Added: Default handler for event selection
};

// Create the uncontrollable version
const UncontrollableCalendar = uncontrollable(Calendar, {
  view: 'onView',
  date: 'onNavigate',
});

export default UncontrollableCalendar;